/**
 * Centralized formatting utilities for consistent display across the application
 */

import { ItemSettingsService } from '../services/ItemSettingsService';

// TODO: Move these to shared instead of core.

/**
 * Format a date string to a localized date format
 * @param dateString - ISO date string or date string
 * @param options - Intl.DateTimeFormatOptions for custom formatting
 * @param valueIfEmpty Value to return if `dateString` is empty
 * @returns Formatted date string
 */
export function formatDate(
	dateString?: string | null,
	options?: Intl.DateTimeFormatOptions,
	valueIfEmpty: string = '-',
): string {
	if (dateString == null || dateString.trim() === '') {
		return valueIfEmpty;
	}

	try {
		// Handle date-only strings (YYYY-MM-DD) and UTC midnight timestamps to avoid timezone shifts
		let date: Date;
		if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
			// Handle YYYY-MM-DD format
			date = new Date(`${dateString}T00:00:00`); // Parse as local date
		} else if (/T00:00:00(\.000)?Z$/.test(dateString)) {
			// Handle UTC midnight timestamps (e.g., 2023-01-31T00:00:00.000Z)
			// Extract just the date part and parse as local date
			const datePart = dateString.substring(0, 10);
			date = new Date(`${datePart}T00:00:00`); // Parse as local date
		} else {
			date = new Date(dateString);
		}
		if (isNaN(date.getTime())) {
			return valueIfEmpty;
		}

		return date.toLocaleDateString(undefined, options);
	} catch {
		return valueIfEmpty;
	}
}

/**
 * Format a date using predefined format strings
 * @param dateString - ISO date string or date string
 * @param format - Predefined format: 'short' | 'medium' | 'long' | 'iso'
 * @param valueIfEmpty Value to return if `dateString` is empty
 * @returns Formatted date string
 */
export function formatDateSimple(
	dateString?: string | null | Date,
	format: 'short' | 'medium' | 'long' | 'iso' | 'mdy' = 'short',
	valueIfEmpty: string = '-',
): string {
	if (
		dateString == null ||
		(typeof dateString === 'string' && dateString.trim() === '')
	) {
		return valueIfEmpty;
	}

	try {
		// Handle date-only strings (YYYY-MM-DD) and UTC midnight timestamps to avoid timezone shifts
		let date: Date;
		if (typeof dateString === 'string') {
			if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
				// Handle YYYY-MM-DD format
				date = new Date(`${dateString}T00:00:00`); // Parse as local date
			} else if (/T00:00:00(\.000)?Z$/.test(dateString)) {
				// Handle UTC midnight timestamps (e.g., 2023-01-31T00:00:00.000Z)
				// Extract just the date part and parse as local date
				const datePart = dateString.substring(0, 10);
				date = new Date(`${datePart}T00:00:00`); // Parse as local date
			} else {
				date = new Date(dateString);
			}
		} else {
			date = dateString;
		}
		if (isNaN(date.getTime())) {
			return valueIfEmpty;
		}

		switch (format) {
			case 'short': {
				// M/D/YY format without zero padding
				const month = date.getMonth() + 1;
				const day = date.getDate();
				const year = date.getFullYear().toString().slice(-2);
				return `${month}/${day}/${year}`;
			}

			case 'mdy': {
				// M/D/YYYY format — same shape as 'short' but with a
				// 4-digit year. Adopted as the licensing surfaces'
				// canonical date format because the 2-digit year on
				// 'short' rendered legitimate year-2000 dates as "00",
				// indistinguishable from a parse bug.
				const month = date.getMonth() + 1;
				const day = date.getDate();
				const year = date.getFullYear();
				return `${month}/${day}/${year}`;
			}

			case 'medium': {
				// Jun 13, 2025
				return date.toLocaleDateString('en-US', {
					month: 'short',
					day: 'numeric',
					year: 'numeric',
				});
			}

			case 'long': {
				// June 13, 2025
				return date.toLocaleDateString('en-US', {
					month: 'long',
					day: 'numeric',
					year: 'numeric',
				});
			}

			case 'iso': {
				// 2025-06-13
				const year = date.getFullYear();
				const month = String(date.getMonth() + 1).padStart(2, '0');
				const day = String(date.getDate()).padStart(2, '0');
				return `${year}-${month}-${day}`;
			}

			default:
				return formatDateSimple(dateString, 'short');
		}
	} catch {
		return valueIfEmpty;
	}
}

/**
 * Format a date string to a localized date and time format
 * @param dateString - ISO date string or date string
 * @param options - Intl.DateTimeFormatOptions for custom formatting
 * @param valueIfEmpty Value to return if `dateString` is empty
 * @returns Formatted date and time string
 */
export function formatDateTime(
	dateString?: string | null,
	options?: Intl.DateTimeFormatOptions,
	valueIfEmpty: string = '-',
): string {
	if (dateString == null || dateString.trim() === '') {
		return valueIfEmpty;
	}

	try {
		// Handle date-only strings (YYYY-MM-DD) and UTC midnight timestamps to avoid timezone shifts
		let date: Date;
		if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
			// Handle YYYY-MM-DD format
			date = new Date(`${dateString}T00:00:00`); // Parse as local date
		} else if (/T00:00:00(\.000)?Z$/.test(dateString)) {
			// Handle UTC midnight timestamps (e.g., 2023-01-31T00:00:00.000Z)
			// Extract just the date part and parse as local date
			const datePart = dateString.substring(0, 10);
			date = new Date(`${datePart}T00:00:00`); // Parse as local date
		} else {
			date = new Date(dateString);
		}
		if (isNaN(date.getTime())) {
			return valueIfEmpty;
		}

		const defaultOptions: Intl.DateTimeFormatOptions = {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
			...options,
		};

		return date.toLocaleString(undefined, defaultOptions);
	} catch {
		return valueIfEmpty;
	}
}

interface FormatCurrencyOptions {
	currency?: string;
	decimals?: number;
	/** Maximum fraction digits, allowing more precision than decimals (minimumFractionDigits) */
	maxDecimals?: number;
	locale?: string;
	valueIfEmpty?: string;
	currencyDisplay?: 'symbol' | 'code' | 'name' | 'narrowSymbol';
	emptyIfZero?: boolean;
}

/**
 * Format a number as currency
 * @param amount - The amount to format
 * @param currency - Currency code (default: 'USD')
 * @param decimals - Number of decimal places (default: 2)
 * @param locale - Locale for formatting (default: 'en-US')
 * @param valueIfEmpty Value to return if `amount` is empty
 * @param currencyDisplay - How to display currency: 'symbol' (default), 'code', or 'name'
 * @returns Formatted currency string
 */
export function formatCurrency(
	amount?: number | string | null,
	currency?: string,
	decimals?: number,
	locale?: string,
	valueIfEmpty?: string,
	currencyDisplay?: 'symbol' | 'code' | 'name' | 'narrowSymbol',
): string;
export function formatCurrency(
	amount?: number | string | null,
	options?: FormatCurrencyOptions,
): string;
export function formatCurrency(
	amount?: number | string | null,
	currencyOrOptions: string | FormatCurrencyOptions = 'USD',
	decimals: number = 2,
	locale: string = 'en-US',
	valueIfEmpty: string = '-',
	currencyDisplay: 'symbol' | 'code' | 'name' | 'narrowSymbol' = 'symbol',
): string {
	let currency: string;
	let emptyIfZero: boolean = false;
	let maxDecimals: number | undefined;

	if (typeof currencyOrOptions === 'object') {
		({
			currency = 'USD',
			decimals = decimals,
			maxDecimals,
			locale = locale,
			valueIfEmpty = valueIfEmpty,
			currencyDisplay = currencyDisplay,
			emptyIfZero = emptyIfZero,
		} = currencyOrOptions);
	} else {
		currency = currencyOrOptions;
	}

	// maxDecimals defaults to decimals when not specified, preserving existing behavior
	const effectiveMaxDecimals = maxDecimals ?? decimals;

	if (typeof amount === 'string') {
		amount = parseFloat(amount);
	}

	if (
		amount == null ||
		isNaN(amount) ||
		!isFinite(amount) ||
		(amount === 0 && emptyIfZero)
	) {
		return valueIfEmpty;
	}

	// Handle -0
	if (Object.is(amount, -0)) {
		amount = 0;
	}

	try {
		return new Intl.NumberFormat(locale, {
			style: 'currency',
			currency,
			currencyDisplay,
			minimumFractionDigits: decimals,
			maximumFractionDigits: effectiveMaxDecimals,
		}).format(amount);
	} catch {
		return `${currency} ${amount.toFixed(2)}`;
	}
}

/**
 * Format a number as a price, using the maxPriceDecimalPrecision preference
 * for maximumFractionDigits. Use this for item prices, line item prices,
 * and cost fields — NOT for subtotals, freight, tax, or totals.
 *
 * @param amount - The price amount to format
 * @param currency - Currency code (default: 'USD')
 * @param locale - Locale for formatting (default: 'en-US')
 * @param valueIfEmpty - Value to return if amount is empty (default: '-')
 * @returns Formatted price string
 */
export function formatPrice(
	amount?: number | string | null,
	currency?: string,
	locale?: string,
	valueIfEmpty?: string,
): string {
	const maxDecimals = ItemSettingsService.getMaxPriceDecimalPrecision();
	return formatCurrency(amount, {
		currency: currency ?? 'USD',
		decimals: 2,
		maxDecimals,
		locale,
		valueIfEmpty,
	});
}

/**
 * Format a number with "auto" decimal precision — strips unnecessary trailing
 * zeros while preserving meaningful precision.
 *
 * For currency mode (isCurrency=true):
 * - Whole numbers show no decimals: 25 → "$25"
 * - Non-whole numbers show at least 2 decimals: 25.1 → "$25.10"
 * - Extra precision is preserved: 25.1014 → "$25.1014"
 *
 * For non-currency mode (isCurrency=false):
 * - Strips all trailing zeros: 25.10 → "25.1", 25.00 → "25"
 *
 * @param amount - The number to format
 * @param isCurrency - Whether to format as currency (enforces min 2 decimals when non-whole)
 * @param currency - Currency code (default: 'USD')
 * @param locale - Locale for formatting (default: 'en-US')
 * @param valueIfEmpty - Value to return if amount is empty (default: '-')
 * @returns Formatted string
 */
export function formatAutoDecimals(
	amount?: number | string | null,
	isCurrency: boolean = false,
	currency: string = 'USD',
	locale: string = 'en-US',
	valueIfEmpty: string = '-',
): string {
	if (typeof amount === 'string') {
		amount = parseFloat(amount);
	}

	if (amount == null || isNaN(amount) || !isFinite(amount)) {
		return valueIfEmpty;
	}

	if (Object.is(amount, -0)) {
		amount = 0;
	}

	// Determine how many significant decimal digits the value has
	const decimalStr = amount.toString();
	const dotIndex = decimalStr.indexOf('.');
	const significantDecimals =
		dotIndex >= 0 ? decimalStr.length - dotIndex - 1 : 0;

	if (isCurrency) {
		// Currency: 0 decimals for whole numbers, at least 2 for non-whole
		const minDecimals = significantDecimals === 0 ? 0 : 2;
		const maxDecimals = Math.max(minDecimals, significantDecimals);

		try {
			return new Intl.NumberFormat(locale, {
				style: 'currency',
				currency,
				minimumFractionDigits: minDecimals,
				maximumFractionDigits: maxDecimals,
			}).format(amount);
		} catch {
			return `$${amount.toFixed(maxDecimals)}`;
		}
	}

	// Non-currency: just strip trailing zeros
	try {
		return new Intl.NumberFormat(locale, {
			minimumFractionDigits: 0,
			maximumFractionDigits: 20,
		}).format(amount);
	} catch {
		return String(amount);
	}
}

/**
 * Format a number with specific decimal places
 * @param value - The number to format
 * @param decimals - Number of decimal places (default: 2)
 * @param locale - Locale for formatting (default: 'en-US')
 * @param valueIfEmpty Value to return if `value` is empty
 * @returns Formatted number string
 */
export function formatNumber(
	value?: number | string | null,
	decimals: number = 2,
	locale: string = 'en-US',
	valueIfEmpty: string = '-',
): string {
	if (typeof value === 'string') {
		value = parseFloat(value);
	}

	if (value == null || isNaN(value) || !isFinite(value)) {
		return valueIfEmpty;
	}

	// Handle -0
	if (Object.is(value, -0)) {
		value = 0;
	}

	try {
		return new Intl.NumberFormat(locale, {
			minimumFractionDigits: decimals,
			maximumFractionDigits: decimals,
		}).format(value);
	} catch {
		return value.toFixed(decimals);
	}
}

/**
 * Format a number as a percentage
 * @param value - The decimal value to format (0.5 = 50%)
 * @param decimals - Number of decimal places (default: 0)
 * @param locale - Locale for formatting (default: 'en-US')
 * @param valueIfEmpty Value to return if `value` is empty
 * @returns Formatted percentage string
 */
export function formatPercentage(
	value?: number | string | null,
	decimals: number = 0,
	locale: string = 'en-US',
	valueIfEmpty: string = '-',
): string {
	if (typeof value === 'string') {
		value = parseFloat(value);
	}

	if (value == null || isNaN(value) || !isFinite(value)) {
		return valueIfEmpty;
	}

	try {
		return new Intl.NumberFormat(locale, {
			style: 'percent',
			minimumFractionDigits: decimals,
			maximumFractionDigits: decimals,
		}).format(value);
	} catch {
		return `${(value * 100).toFixed(decimals)}%`;
	}
}

export function formatPhoneNumber(
	phone: string | null | undefined,
	options?: {
		valueIfEmpty?: string;
		format?: 'displayWithCountryCode' | 'displayWithoutCountryCode' | 'E.164';
	},
): string;
export function formatPhoneNumber(
	phone: string | null | undefined,
	// eslint-disable-next-line @typescript-eslint/unified-signatures
	valueIfEmpty?: string,
): string;
export function formatPhoneNumber(
	phone: string | null | undefined,
	optionsOrValueIfEmpty:
		| string
		| {
				valueIfEmpty?: string;
				format?:
					| 'displayWithCountryCode'
					| 'displayWithoutCountryCode'
					| 'E.164';
		  } = '-',
): string {
	let options: {
		valueIfEmpty?: string;
		format?: 'displayWithCountryCode' | 'displayWithoutCountryCode' | 'E.164';
	};

	if (typeof optionsOrValueIfEmpty === 'string') {
		options = {
			valueIfEmpty: optionsOrValueIfEmpty,
		};
	} else {
		options = optionsOrValueIfEmpty;
	}

	options.valueIfEmpty ??= '-';
	options.format ??= 'displayWithCountryCode';

	if (phone == null || phone.trim() === '') {
		return options.valueIfEmpty;
	}

	const digits = phone.replace(/\D/g, '');

	if (digits.length === 0) {
		return options.valueIfEmpty;
	}

	let countryCode: string;
	let nationalNumber: string;

	if (digits.length === 10) {
		countryCode = '1';
		nationalNumber = digits;
	} else if (digits.length === 11 && digits[0] === '1') {
		countryCode = '1';
		nationalNumber = digits.slice(1);
	} else if (digits.length > 10) {
		countryCode = digits.slice(0, digits.length - 10);
		nationalNumber = digits.slice(-10);
	} else {
		return options.valueIfEmpty;
	}

	if (nationalNumber.length !== 10) {
		return options.valueIfEmpty;
	}

	const areaCode = nationalNumber.slice(0, 3);
	const exchange = nationalNumber.slice(3, 6);
	const subscriber = nationalNumber.slice(6);

	switch (options.format) {
		case 'E.164':
			return `+${countryCode}${nationalNumber}`;

		case 'displayWithoutCountryCode':
			return `(${areaCode}) ${exchange}-${subscriber}`;

		case 'displayWithCountryCode':
		default:
			return `+${countryCode} (${areaCode}) ${exchange}-${subscriber}`;
	}
}

/**
 * Normalize a free-form phone number to canonical E.164 format
 * (`+<countryCode><nationalNumber>`). Returns `null` when the input
 * is null/empty or cannot be parsed into a valid phone shape.
 *
 * Why this exists alongside `formatPhoneNumber`: the display formatter
 * returns a sentinel string (`'-'` by default) on unparseable input so
 * UIs can render something readable. The mapping-table sync utilities
 * need a strict pass/fail signal so they can drop unparseable rows
 * rather than silently store the placeholder, which would poison the
 * caller-ID matcher's exact-match join.
 *
 * Heuristics match the existing `formatPhoneNumber` for consistency:
 *   - 10 digits → assumed US/Canada, country code `1`
 *   - 11 digits starting with `1` → US/Canada with leading 1 stripped
 *   - 12-15 digits → variable-length country code, last 10 are national
 * Anything else (too few digits, extension suffixes that pad past 15,
 * gibberish like "see notes") returns null.
 */
export function normalizeToE164(
	phone: string | null | undefined,
): string | null {
	if (phone == null) return null;

	const trimmed = phone.trim();

	if (trimmed === '') return null;

	// Drop any trailing extension (ext / x / #) BEFORE collapsing to digits.
	// Folding an extension's digits into the number both corrupts the number
	// (a leading digit gets misread as a country code) and silently swallows
	// the extension. Extensions live in a separate column — see
	// `parsePhoneWithExtension`. PD-14494.
	const { base } = splitPhoneExtension(trimmed);

	const digits = base.replace(/\D/g, '');

	if (digits.length < 10 || digits.length > 15) return null;

	let countryCode: string;
	let nationalNumber: string;

	if (digits.length === 10) {
		countryCode = '1';
		nationalNumber = digits;
	} else if (digits.length === 11 && digits.startsWith('1')) {
		countryCode = '1';
		nationalNumber = digits.slice(1);
	} else {
		countryCode = digits.slice(0, digits.length - 10);
		nationalNumber = digits.slice(-10);
	}

	if (nationalNumber.length !== 10) return null;

	return `+${countryCode}${nationalNumber}`;
}

/**
 * Split a trailing phone extension off the base number. Recognizes the common
 * markers (`ext`, `extension`, `x`, `#`) followed by the extension digits at
 * the end of the string. Returns the base (still un-normalized) and the
 * extension digits (or null when there is no extension). Helper for
 * `normalizeToE164` / `parsePhoneWithExtension` / the country-aware dialer
 * normalization in `phone-country.ts`. PD-14494.
 */
export function splitPhoneExtension(trimmed: string): {
	base: string;
	extension: string | null;
} {
	const match = trimmed.match(
		/^(.*?)\s*(?:ext(?:ension)?\.?|x|#)[\s:.#-]*(\d+)\s*$/i,
	);

	if (match == null) {
		return { base: trimmed, extension: null };
	}

	return { base: match[1] ?? '', extension: match[2] ?? null };
}

/**
 * Parse a raw phone string into its canonical E.164 base number and a separate
 * extension. E.164 cannot encode an extension, so callers that need to retain
 * one store `e164` in the phone column and `extension` in a dedicated column.
 * `e164` is null when the base cannot be parsed into a valid phone shape.
 *
 * NOTE: a behaviorally-identical twin lives in `libs/shared`'s `validation.ts`
 * (`parsePhoneWithExtension`), because libs/shared cannot import from libs/core.
 * Keep both in sync. PD-14494.
 */
export function parsePhoneWithExtension(phone: string | null | undefined): {
	e164: string | null;
	extension: string | null;
} {
	if (phone == null) {
		return { e164: null, extension: null };
	}

	const trimmed = phone.trim();
	if (trimmed === '') {
		return { e164: null, extension: null };
	}

	const { base, extension } = splitPhoneExtension(trimmed);

	return { e164: normalizeToE164(base), extension };
}

/**
 * Format bytes to human readable size
 * @param bytes - Number of bytes
 * @param decimals - Number of decimal places (default: 2)
 * @param valueIfEmpty Value to return if `bytes` is empty
 * @returns Formatted size string
 */
export function formatFileSize(
	bytes?: number | null,
	decimals: number = 2,
	valueIfEmpty: string = '-',
): string {
	if (bytes == null || bytes === 0) {
		return '0 Bytes';
	}
	if (isNaN(bytes) || !isFinite(bytes) || bytes < 0) {
		return valueIfEmpty;
	}

	const k = 1024;
	const dm = decimals < 0 ? 0 : decimals;
	const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

	const i = Math.floor(Math.log(bytes) / Math.log(k));
	// Clamp index to valid range
	const clampedIndex = Math.min(i, sizes.length - 1);

	return `${parseFloat((bytes / Math.pow(k, clampedIndex)).toFixed(dm))} ${sizes[clampedIndex]}`;
}

/**
 * Format a duration in seconds to human readable format
 * @param seconds - Duration in seconds
 * @param valueIfEmpty Value to return if `seconds` is empty
 * @returns Formatted duration string
 */
export function formatDuration(
	seconds?: number | null,
	valueIfEmpty: string = '-',
): string {
	if (seconds == null || isNaN(seconds) || !isFinite(seconds) || seconds < 0) {
		return valueIfEmpty;
	}

	const hours = Math.floor(seconds / 3600);
	const minutes = Math.floor((seconds % 3600) / 60);
	const secs = Math.floor(seconds % 60);

	if (hours > 0) {
		return `${hours}h ${minutes}m ${secs}s`;
	} else if (minutes > 0) {
		return `${minutes}m ${secs}s`;
	} else {
		return `${secs}s`;
	}
}

/**
 * Format a duration in minutes to human readable format (hours and minutes)
 * @param minutes - Duration in minutes
 * @returns Formatted duration string (e.g., "1h 30m" or "45m")
 */
export function formatMinutesToDuration(minutes?: number | null): string {
	if (minutes == null || isNaN(minutes) || !isFinite(minutes) || minutes < 0) {
		return '-';
	}

	const roundedMinutes = Math.round(minutes);
	const hours = Math.floor(roundedMinutes / 60);
	const remainingMinutes = roundedMinutes % 60;

	if (hours > 0) {
		if (remainingMinutes > 0) {
			return `${hours}h ${remainingMinutes}m`;
		}

		return `${hours}h`;
	}

	return `${roundedMinutes}m`;
}

/**
 * Format a date as relative time (e.g., "2 hours ago")
 * @param dateString - ISO date string or date string
 * @param valueIfEmpty Value to return if `dateString` is empty
 * @returns Relative time string or formatted date if too old
 */
export function formatRelativeTime(
	dateString?: string | null,
	valueIfEmpty: string = '-',
): string {
	if (dateString == null || dateString.trim() === '') {
		return valueIfEmpty;
	}

	try {
		// Handle date-only strings (YYYY-MM-DD) and UTC midnight timestamps to avoid timezone shifts
		let date: Date;
		if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
			// Handle YYYY-MM-DD format
			date = new Date(`${dateString}T00:00:00`); // Parse as local date
		} else if (/T00:00:00(\.000)?Z$/.test(dateString)) {
			// Handle UTC midnight timestamps (e.g., 2023-01-31T00:00:00.000Z)
			// Extract just the date part and parse as local date
			const datePart = dateString.substring(0, 10);
			date = new Date(`${datePart}T00:00:00`); // Parse as local date
		} else {
			date = new Date(dateString);
		}
		if (isNaN(date.getTime())) {
			return valueIfEmpty;
		}

		const now = new Date();
		const diffMs = now.getTime() - date.getTime();
		const diffSecs = Math.floor(diffMs / 1000);
		const diffMins = Math.floor(diffSecs / 60);
		const diffHours = Math.floor(diffMins / 60);
		const diffDays = Math.floor(diffHours / 24);

		if (diffSecs < 60) {
			return 'just now';
		} else if (diffMins < 60) {
			return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
		} else if (diffHours < 24) {
			return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
		} else if (diffDays < 7) {
			return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
		} else {
			return formatDate(dateString);
		}
	} catch {
		return valueIfEmpty;
	}
}

/**
 * Format how long ago a date was, in whole CALENDAR days, always as a
 * relative phrase regardless of how old it is. Unlike `formatRelativeTime`
 * (which falls back to an absolute date past a week), this always answers
 * "how many days ago" — used for the driver "Last Delivery" hint (PD-13763)
 * where the delivery cadence is typically weekly/biweekly and the day count
 * is the point.
 *
 * Returns `'today'`, `'yesterday'`, or `'N days ago'`. Comparison is on
 * local calendar days (not 24h windows) so a delivery late yesterday reads
 * "yesterday", not "today". A future date (clock skew) clamps to `'today'`.
 *
 * @param dateString - ISO date string or date string
 * @param referenceDate - "now" to compare against; injectable for tests
 * @returns Relative-days phrase, or `null` when the input is empty/invalid
 */
export function formatDaysAgo(
	dateString?: string | null,
	referenceDate: Date = new Date(),
): string | null {
	if (dateString == null || dateString.trim() === '') {
		return null;
	}

	const date = new Date(dateString);
	if (isNaN(date.getTime())) {
		return null;
	}

	// Compare on local calendar-day boundaries so partial days don't skew
	// the count (a delivery at 11pm yesterday is "yesterday", not "today").
	const startOfDay = (d: Date): number =>
		new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
	const diffDays = Math.round(
		(startOfDay(referenceDate) - startOfDay(date)) / 86_400_000,
	);

	if (diffDays <= 0) {
		return 'today';
	}
	if (diffDays === 1) {
		return 'yesterday';
	}
	return `${diffDays} days ago`;
}

/**
 * Format a boolean value to Yes/No
 * @param value - Boolean value
 * @param trueText - Text for true (default: 'Yes')
 * @param falseText - Text for false (default: 'No')
 * @param valueIfEmpty Value to return if `value` is empty
 * @returns Formatted boolean string
 */
export function formatBoolean(
	value?: boolean | null,
	trueText: string = 'Yes',
	falseText: string = 'No',
	valueIfEmpty: string = '-',
): string {
	if (value == null) {
		return valueIfEmpty;
	}
	return value ? trueText : falseText;
}

/**
 * Format an address into an array of lines
 * @param address - Address object with optional fields
 * @param valueIfEmpty - The value to return if data is not available
 * @param options - The number of lines to return (1, 2, or 3)
 * @returns Array of formatted address lines
 */
export function formatAddress(
	address: {
		address1?: string | null;
		address2?: string | null;
		city?: string | null;
		state?: string | null;
		zip?: string | null;
	},
	valueIfEmpty: Array<string> = ['No address on file'],
	options?: {
		lines?: 1 | 2 | 3;
	},
): string[] {
	options ??= {};
	options.lines ??= 1;

	const parts: string[] = [];

	// Add street addresses with bullet separator
	if (address.address1) {
		parts.push(address.address1);
	}
	if (address.address2) {
		parts.push(address.address2);
	}

	// Build city, state, zip part
	if (address.city) {
		if (address.state) {
			// City and state together with proper comma
			const zipPart = address.zip ? ` ${address.zip}` : '';
			parts.push(`${address.city}, ${address.state}${zipPart}`);
		} else {
			// Just city (and maybe zip)
			const cityZip = address.zip
				? `${address.city} ${address.zip}`
				: address.city;
			parts.push(cityZip);
		}
	} else if (address.state || address.zip) {
		// No city but have state or zip
		const stateZip: string[] = [];
		if (address.state) {
			stateZip.push(address.state);
		}
		if (address.zip) {
			stateZip.push(address.zip);
		}
		if (stateZip.length > 0) {
			parts.push(stateZip.join(' '));
		}
	}

	if (options.lines === 1 && parts.length > 0) {
		return [parts.join(' • ')];
	} else if (options.lines === 2) {
		if (parts.length === 3) {
			parts[0] = `${parts[0]}, ${parts[1]}`;
			parts.splice(1, 1);
		}

		if (parts.length > 0) {
			return parts;
		}
	} else if (parts.length > 0) {
		return parts;
	}

	return valueIfEmpty;
}

/**
 * Format an address as a single line string
 * @param address - Address object with optional fields
 * @param separator - Separator between address parts (default: ', ')
 * @returns Formatted address string
 */
export function formatAddressInline(
	address: {
		address1?: string | null;
		address2?: string | null;
		city?: string | null;
		state?: string | null;
		zip?: string | null;
	},
	separator: string = ', ',
): string {
	const lines = formatAddress(address);
	return lines.join(separator);
}

/**
 * Map of IANA timezone identifiers to common abbreviations
 */
const TIMEZONE_ABBREVIATIONS: Record<string, string> = {
	'America/New_York': 'ET',
	'America/Chicago': 'CT',
	'America/Denver': 'MT',
	'America/Los_Angeles': 'PT',
	'America/Phoenix': 'AZ',
	'America/Anchorage': 'AKT',
	'Pacific/Honolulu': 'HT',
	UTC: 'UTC',
};

/**
 * Get timezone abbreviation from IANA timezone identifier
 * @param timezone - IANA timezone identifier (e.g., 'America/New_York')
 * @returns Timezone abbreviation (e.g., 'ET'), defaults to 'ET' if not found
 */
export function getTimezoneAbbreviation(
	timezone: string | null | undefined,
): string {
	if (timezone == null || timezone.trim() === '') {
		return 'ET'; // Default to Eastern Time
	}

	return TIMEZONE_ABBREVIATIONS[timezone] ?? 'ET';
}

/**
 * Extract and format time from an ISO datetime string
 * @param dateString - ISO datetime string (e.g., '2024-12-16T14:30:00.000Z')
 * @param valueIfEmpty - Value to return if no time component
 * @returns Formatted time string (e.g., '2:30 PM') or null if no time component
 */
export function formatTimeFromISO(
	dateString: string | null | undefined,
	valueIfEmpty: string | null = null,
): string | null {
	if (dateString == null || dateString.trim() === '') {
		return valueIfEmpty;
	}

	// Check if it's a date-only string (no time component)
	if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
		return valueIfEmpty;
	}

	// Check if it's a midnight timestamp (treat as no time)
	if (/T00:00:00(\.000)?Z?$/.test(dateString)) {
		return valueIfEmpty;
	}

	try {
		const date = new Date(dateString);

		if (isNaN(date.getTime())) {
			return valueIfEmpty;
		}

		return date.toLocaleTimeString('en-US', {
			hour: 'numeric',
			minute: '2-digit',
			hour12: true,
		});
	} catch {
		return valueIfEmpty;
	}
}

/**
 * Check if an ISO datetime string has a meaningful time component (not midnight)
 * @param dateString - ISO datetime string
 * @returns true if the string has a non-midnight time component
 */
export function hasTimeComponent(
	dateString: string | null | undefined,
): boolean {
	if (dateString == null || dateString.trim() === '') {
		return false;
	}

	// Date-only strings have no time
	if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
		return false;
	}

	// Midnight timestamps are treated as "no time"
	if (/T00:00:00(\.000)?Z?$/.test(dateString)) {
		return false;
	}

	// Has a T indicating datetime format with non-midnight time
	return dateString.includes('T');
}

/**
 * Format a number in compact notation (e.g., 1.2M, 5.5K)
 * Useful for displaying large numbers in limited space like dashboards, charts, and KPI cards
 * @param value - The number to format
 * @param options - Formatting options
 * @param options.decimals - Number of decimal places (default: 1)
 * @param options.threshold - Minimum value to start abbreviating (default: 1000)
 * @param options.locale - Locale for number formatting (default: 'en-US')
 * @param options.valueIfEmpty - Value to return if empty (default: '-')
 * @returns Formatted compact number string (e.g., "1.2M", "5.5K", "123")
 */
export function formatCompactNumber(
	value?: number | string | null,
	options: {
		decimals?: number;
		threshold?: number;
		locale?: string;
		valueIfEmpty?: string;
	} = {},
): string {
	const {
		decimals = 1,
		threshold = 1000,
		locale = 'en-US',
		valueIfEmpty = '-',
	} = options;

	if (typeof value === 'string') {
		value = parseFloat(value);
	}

	if (value == null || isNaN(value) || !isFinite(value)) {
		return valueIfEmpty;
	}

	const absValue = Math.abs(value);

	if (absValue < threshold) {
		return Math.round(value).toLocaleString(locale);
	}

	if (absValue >= 1_000_000_000) {
		return `${(value / 1_000_000_000).toFixed(decimals)}B`;
	}

	if (absValue >= 1_000_000) {
		return `${(value / 1_000_000).toFixed(decimals)}M`;
	}

	if (absValue >= 1_000) {
		// No decimals for 100K - 999K range
		if (absValue >= 100_000 && absValue < 1_000_000) {
			const roundedK = Math.round(value / 1_000);

			// If rounding produces >= 1000K, show as M instead
			if (Math.abs(roundedK) >= 1000) {
				return `${(value / 1_000_000).toFixed(decimals)}M`;
			}

			return `${roundedK}K`;
		}

		// Use decimals for < 100K
		return `${(value / 1_000).toFixed(decimals)}K`;
	}

	return Math.round(value).toLocaleString(locale);
}

/**
 * Format user display name from user object
 * @param user - User object with optional name fields
 * @param fallback - Fallback value if no name available (default: 'User')
 * @returns Formatted name: "First Last", email, or fallback
 */
export function formatUserName(
	user:
		| {
				firstName?: string | null;
				lastName?: string | null;
				email?: string | null;
		  }
		| null
		| undefined,
	fallback: string = 'User',
): string {
	if (user == null) {
		return fallback;
	}

	const fullName = `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim();

	if (fullName.length > 0) {
		return fullName;
	}

	if (user.email != null && user.email.length > 0) {
		return user.email;
	}

	return fallback;
}

/**
 * Result of formatting a task due date
 */
export interface FormatDueDateResult {
	dateText: string;
	timeText: string | null;
	icon: 'error' | 'warning' | null;
}

/**
 * Format a task due date with status indicator
 * @param dueDate - Due date string (ISO format or date string)
 * @param dueTime - Optional due time string (HH:MM format)
 * @param options - Optional configuration
 * @returns Object with formatted date text, time text, and status icon
 *
 * Icon meanings:
 * - 'error': Task is overdue (past due date/time)
 * - 'warning': Task is due today or within 2 hours
 * - null: Task is not urgent
 */
export function formatDueDate(
	dueDate: string | null | undefined,
	dueTime?: string | null,
	options?: {
		noDateText?: string;
		allDayText?: string;
	},
): FormatDueDateResult {
	const noDateText = options?.noDateText ?? 'No due date';
	const allDayText = options?.allDayText ?? 'All Day';

	if (dueDate == null) {
		return { dateText: noDateText, timeText: null, icon: null };
	}

	// Handle date-only strings (YYYY-MM-DD) and UTC midnight timestamps to avoid timezone shifts
	let due: Date;
	if (/^\d{4}-\d{2}-\d{2}$/.test(dueDate)) {
		// Handle YYYY-MM-DD format - parse as local date
		due = new Date(`${dueDate}T00:00:00`);
	} else if (/T00:00:00(\.000)?Z$/.test(dueDate)) {
		// Handle UTC midnight timestamps (e.g., 2023-01-31T00:00:00.000Z)
		// Extract just the date part and parse as local date
		const datePart = dueDate.substring(0, 10);
		due = new Date(`${datePart}T00:00:00`);
	} else {
		due = new Date(dueDate);
	}

	const today = new Date();
	today.setHours(0, 0, 0, 0);

	const now = new Date();

	// Determine icon status
	let icon: 'error' | 'warning' | null = null;

	if (due < today) {
		// Overdue (past due date)
		icon = 'error';
	} else if (due.toDateString() === today.toDateString()) {
		// Due today
		if (dueTime == null) {
			// All-day task due today = warning
			icon = 'warning';
		} else {
			// Has specific time - check if already passed or within 2 hours
			const timeParts = dueTime.split(':');
			const dueDateTime = new Date(due);
			dueDateTime.setHours(Number(timeParts[0]), Number(timeParts[1]), 0, 0);

			if (dueDateTime < now) {
				// Time has passed - overdue
				icon = 'error';
			} else {
				const twoHoursFromNow = new Date(now.getTime() + 2 * 60 * 60 * 1000);

				if (dueDateTime <= twoHoursFromNow) {
					icon = 'warning';
				}
			}
		}
	}

	// Format date as "Mon 12/30"
	const dayName = due.toLocaleDateString('en-US', { weekday: 'short' });
	const dateStr = `${due.getMonth() + 1}/${due.getDate()}`;

	// Format time if available (dueTime is null for all-day tasks)
	let timeText: string | null;

	if (dueTime != null) {
		const timeParts = dueTime.split(':');
		const hours = Number(timeParts[0]);
		const minutes = Number(timeParts[1]);
		const period = hours >= 12 ? 'PM' : 'AM';
		const hourMod = hours % 12;
		const displayHours = hourMod === 0 ? 12 : hourMod;
		timeText = `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
	} else {
		timeText = allDayText;
	}

	return {
		dateText: `${dayName} ${dateStr}`,
		timeText,
		icon,
	};
}
